import { useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Bot, Info, Users } from "lucide-react";

import { AgentAboutDialog } from "@/components/agent-about-dialog";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Agent } from "@/hooks/use-agents";
import { cn, type AccessTokenGetter } from "@/lib/utils";

interface AgentBadgeProps {
  agentId: string | null;
  agentName?: string;
  type?: "agent" | "team";
  className?: string;
  size?: "sm" | "md";
}

export function AgentBadge({
  agentId,
  agentName,
  type = "agent",
  className,
  size = "sm",
}: AgentBadgeProps) {
  if (!agentId) return null;

  const label = agentName || agentId;
  const Icon = type === "team" ? Users : Bot;

  return (
    <Badge
      asChild
      variant="secondary"
      className={cn(
        "gap-1 px-1.5 py-0.5 font-medium",
        size === "md" && "px-2 py-1 text-sm",
        className
      )}
    >
      <span title={agentId}>
        <Icon className={cn("shrink-0", size === "sm" ? "size-3" : "size-4")} />
        <span className="hidden max-w-[80px] truncate group-hover:inline-block">{label}</span>
      </span>
    </Badge>
  );
}

interface AgentInfoTagProps {
  /** The selected agent's id, including a custom ref when not in the catalog. */
  agentId: string | null;
  /** Optional directory metadata supplied by the accessible-agents response. */
  agent?: Agent;
  /** Needed by the standard capabilities dialog. */
  getToken?: AccessTokenGetter;
  /** Use a shorter label before moving the control into header overflow. */
  compact?: boolean;
  className?: string;
}

/**
 * Read-only selected-agent display for conversations that have already
 * started. It deliberately uses the same AgentAboutDialog as AgentDropdown,
 * so the info affordance remains available after agent selection locks.
 */
export function AgentInfoTag({
  agentId,
  agent,
  getToken,
  compact = false,
  className,
}: AgentInfoTagProps) {
  const { t } = useTranslation();
  const [aboutOpen, setAboutOpen] = useState(false);

  if (!agentId) return null;

  const label = agent?.name?.trim() || agentId;
  const aboutLabel = t("agent.aboutAgent", { name: label });
  const configErrorLabel = agent?.configError
    ? t("agent.configError", { error: agent.configError })
    : null;
  const isTeam = agent?.type === "team";
  const Icon = isTeam ? Users : Bot;
  const openAbout = () => setAboutOpen(true);
  const handleTagKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openAbout();
  };

  return (
    <TooltipProvider>
      <Badge
        asChild
        variant="secondary"
        className={cn(
          "min-w-0 max-w-full gap-1 px-1.5 py-0.5 font-medium",
          getToken && "cursor-pointer",
          className,
        )}
      >
        <span
          data-testid="conversation-agent-info-tag"
          title={getToken ? aboutLabel : agentId}
          role={getToken ? "button" : undefined}
          tabIndex={getToken ? 0 : undefined}
          aria-haspopup={getToken ? "dialog" : undefined}
          aria-expanded={getToken ? aboutOpen : undefined}
          aria-label={getToken ? aboutLabel : undefined}
          onClick={getToken ? openAbout : undefined}
          onKeyDown={getToken ? handleTagKeyDown : undefined}
        >
          <Icon className="size-3 shrink-0" aria-hidden="true" />
          <span className={cn("min-w-0 truncate", compact ? "max-w-[88px]" : "max-w-[140px]")}>{label}</span>
          {configErrorLabel && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="inline-flex shrink-0 text-amber-500"
                  aria-label={configErrorLabel}
                  title={configErrorLabel}
                >
                  <AlertTriangle className="size-3" aria-hidden="true" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="max-w-[320px] whitespace-pre-wrap text-xs">
                  {configErrorLabel}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
          {getToken && (
            <button
              type="button"
              className="-mr-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-sm opacity-75 transition-colors hover:bg-background/60 hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label={aboutLabel}
              title={aboutLabel}
              onClick={(event) => {
                event.stopPropagation();
                openAbout();
              }}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Info className="size-3" aria-hidden="true" />
            </button>
          )}
        </span>
      </Badge>
      {getToken && (
        <AgentAboutDialog
          agentId={agentId}
          agent={agent}
          open={aboutOpen}
          onOpenChange={setAboutOpen}
          getToken={getToken}
        />
      )}
    </TooltipProvider>
  );
}
